import { Plus } from 'lucide-react';

import RestaurantTable from '../../../shared/RestaurantTable';
import RestaurantTableActions from '../../../shared/RestaurantTableActions';
import { RestaurantModifierGroupDTO } from '../../../../../types/restaurant/RestaurantCatalogExtensionTypes';

interface Props {
  groups: RestaurantModifierGroupDTO[];
  onCreate: () => void;
  onEdit: (group: RestaurantModifierGroupDTO) => void;
  onToggleActive: (group: RestaurantModifierGroupDTO) => void;
}

const ModifierGroupsTable = ({
  groups,
  onCreate,
  onEdit,
  onToggleActive,
}: Props) => {
  const columns = [
    {
      key: 'name',
      header: 'Grupo',
      cell: (row: RestaurantModifierGroupDTO) => (
        <div>
          <p className="font-medium text-black dark:text-white">{row.name}</p>
          {row.description && (
            <p className="mt-0.5 line-clamp-1 text-xs text-bodydark2">
              {row.description}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'range',
      header: 'Selección',
      className: 'text-sm text-bodydark2',
      cell: (row: RestaurantModifierGroupDTO) =>
        `${row.minSelections}–${row.maxSelections}`,
    },
    {
      key: 'required',
      header: 'Tipo',
      cell: (row: RestaurantModifierGroupDTO) => (
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            row.required
              ? 'bg-warning/15 text-warning'
              : 'bg-gray-2 text-bodydark2 dark:bg-meta-4'
          }`}
        >
          {row.required ? 'Obligatorio' : 'Opcional'}
        </span>
      ),
    },
    {
      key: 'options',
      header: 'Opciones',
      className: 'text-sm text-bodydark2',
      cell: (row: RestaurantModifierGroupDTO) => row.modifiers.length,
    },
    {
      key: 'active',
      header: 'Estado',
      cell: (row: RestaurantModifierGroupDTO) => (
        <span
          className={`text-[11px] font-semibold ${
            row.active ? 'text-success' : 'text-danger'
          }`}
        >
          {row.active ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      cell: (row: RestaurantModifierGroupDTO) => (
        <RestaurantTableActions
          onEdit={() => onEdit(row)}
          onToggle={() => onToggleActive(row)}
          active={row.active}
        />
      ),
    },
  ];

  return (
    <div className="p-5 sm:p-7">
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
        >
          <Plus className="h-4 w-4" />
          Nuevo grupo
        </button>
      </div>

      {groups.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-stroke p-8 text-center text-sm text-bodydark2 dark:border-strokedark">
          No hay grupos de modificadores con estos filtros.
        </p>
      ) : (
        <>
          <RestaurantTable
            columns={columns}
            rows={groups}
            rowKey={(row) => row.id}
            rowClassName={(row) => (row.active ? '' : 'opacity-60')}
            minWidth="min-w-[760px]"
          />

          <div className="space-y-3 md:hidden">
            {groups.map((group) => (
              <div
                key={group.id}
                className="rounded-xl border border-stroke p-4 dark:border-strokedark"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-black dark:text-white">
                    {group.name}
                  </p>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      group.required
                        ? 'bg-warning/15 text-warning'
                        : 'bg-gray-2 text-bodydark2 dark:bg-meta-4'
                    }`}
                  >
                    {group.required ? 'Obligatorio' : 'Opcional'}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-bodydark2">
                  {group.minSelections}–{group.maxSelections} ·{' '}
                  {group.modifiers.length} opción
                  {group.modifiers.length === 1 ? '' : 'es'} ·{' '}
                  {group.active ? 'Activo' : 'Inactivo'}
                </p>
                <div className="mt-3">
                  <RestaurantTableActions
                    onEdit={() => onEdit(group)}
                    onToggle={() => onToggleActive(group)}
                    active={group.active}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ModifierGroupsTable;
